import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit'
import axios from 'axios'
import { apiRoot } from '../config'

interface TranslateRowState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  result: string | null;
}

interface TranslateState {
  [key: string]: TranslateRowState;
}

const initialState: TranslateState = {}

interface TranslateProps {
  text: string;
  source: string;
  target: string;
  key: string;
}

export const translate = createAsyncThunk(
  'translate/translate',
  async ({ text, source, target, key }: TranslateProps) => {
    const params = new URLSearchParams({ source, target, text })
    const response = await axios.get(apiRoot + `/api/translate/?${params.toString()}`)
    return { key, data: response.data }
  }
)

const translateSlice = createSlice({
  name: 'translate',
  initialState,
  reducers: {
    resetTranslate(state, action) {
      const key = action.payload
      if (key && state[key]) {
        state[key] = { status: 'idle', error: null, result: null }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(translate.pending, (state, action) => {
        const key = action.meta.arg.key
        state[key] = { status: 'loading', error: null, result: null }
      })
      .addCase(translate.fulfilled, (state, action) => {
        const key = action.payload.key
        state[key] = {
          status: 'succeeded',
          error: null,
          result: action.payload.data.result || ''
        }
      })
      .addCase(translate.rejected, (state, action) => {
        const key = action.meta.arg.key
        state[key] = {
          status: 'failed',
          error: action.error.message || '',
          result: null
        }
      })
  },
})

export const { resetTranslate } = translateSlice.actions

// Default translate state
const defaultTranslateState: TranslateRowState = {
  status: 'idle',
  error: null,
  result: null,
}

// Memoized selector factory for getting translate state by key
export const makeSelectTranslateState = (key: string) =>
  createSelector(
    [(state: { translate: TranslateState }) => state.translate[key]],
    (translateState) => translateState || defaultTranslateState
  )

export default translateSlice.reducer
